import React from 'react';
import styles from './style.module.scss';
import StatCard from 'components/pages/area-listings/components/market-insights/stat-card';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import { useTranslation } from 'next-i18next';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';

import type { InsightsResponse } from '@zoocasa/go-search';

export interface MarketStatsProps {
  /** The insights data containing market statistics */
  insightsData?: InsightsResponse;
  /** The name of the area for display */
  areaName?: string;
  /** Whether to show rental stats (defaults to true) */
  showRentals?: boolean;
}

/**
 * Displays key market statistics for an area including median price,
 * days on market, and price per square foot.
 * Only renders if at least one meaningful stat value is available.
 */
const MarketStats = ({ insightsData, areaName, showRentals = true }: MarketStatsProps) => {
  const { formatCurrency } = useI18nFormatting();
  const { t } = useTranslation(Namespaces.COMMON);

  if (!insightsData) {
    return null;
  }

  const medianPrice = insightsData.MedianPurchasePrice ?? 0;
  const medianRentPrice = insightsData.MedianRentPrice ?? 0;
  const daysOnMarket = insightsData.DaysOnMarket ?? 0;
  const avgPricePerSqft = insightsData.AvgPricePerSqft ?? 0;
  const sellListRatio = insightsData.SellListRatio ?? 0;

  // Don't render the section if ALL stat values are 0 (no meaningful data)
  const hasAnyStatValue = medianPrice > 0 || daysOnMarket > 0 || avgPricePerSqft > 0 || 
    (showRentals && medianRentPrice > 0) || sellListRatio > 0;

  if (!hasAnyStatValue) {
    return null;
  }

  return (
    <section className={styles.component}>
      <h2 className={styles.heading}>
        {areaName
          ? t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_HEADINGWITHAREA, { areaName, defaultValue: '{{areaName}} Market Statistics' })
          : t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_HEADING, { defaultValue: 'Market Statistics' })}
      </h2>
      <div className={styles.statsGrid}>
        <StatCard
          title={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_MEDIANHOMEPRICETITLE, { defaultValue: 'Median Home Price' })}
          formattedValue={formatCurrency(medianPrice)}
          showNotAvailable={medianPrice <= 0}
          tooltipContent={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_MEDIANHOMEPRICETOOLTIP, { defaultValue: 'The price at which half the homes sold for more and half sold for less in the last 90 days' })}
          period={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_PAST90DAYS, { defaultValue: 'Past 90 days' })}
        />
        {showRentals && (
          <StatCard
            title={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_MEDIANRENTPRICETITLE, { defaultValue: 'Median Rent Price' })}
            formattedValue={formatCurrency(medianRentPrice)}
            showNotAvailable={medianRentPrice <= 0}
            tooltipContent={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_MEDIANRENTPRICETOOLTIP, { defaultValue: 'The median monthly rent price for rental properties in the last 90 days' })}
            period={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_PAST90DAYS, { defaultValue: 'Past 90 days' })}
          />
        )}
        <StatCard
          title={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_AVGDAYSONMARKETTITLE, { defaultValue: 'Avg. Days on Market' })}
          formattedValue={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_AVGDAYSONMARKETVALUE, { days: String(daysOnMarket), defaultValue: '{{days}} days' })}
          showNotAvailable={daysOnMarket <= 0}
          tooltipContent={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_AVGDAYSONMARKETTOOLTIP, { defaultValue: 'The average number of days a property was actively listed for sale before going under contract' })}
          period={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_PAST90DAYS, { defaultValue: 'Past 90 days' })}
        />
        <StatCard
          title={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_PRICEPERSQFTTITLE, { defaultValue: 'Price per Sq.Ft' })}
          formattedValue={formatCurrency(avgPricePerSqft)}
          showNotAvailable={avgPricePerSqft <= 0}
          tooltipContent={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_PRICEPERSQFTTOOLTIP, { defaultValue: 'The average price for each square foot of living space sold' })}
          period={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_PAST90DAYS, { defaultValue: 'Past 90 days' })}
        />
        {sellListRatio > 0 && (
          <StatCard
            title={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_SALETOLISTRATIOTITLE, { defaultValue: 'Sale-to-List Ratio' })}
            formattedValue={`${(sellListRatio * 100).toFixed(1)}%`}
            showNotAvailable={sellListRatio <= 0}
            tooltipContent={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_SALETOLISTRATIOTOOLTIP, { defaultValue: 'The average percentage of list price that homes sell for. Over 100% indicates homes selling above asking price' })}
            period={t(LOCALE_COMMON_PATHS.CITYINFO_MARKETSTATS_PAST90DAYS, { defaultValue: 'Past 90 days' })}
          />
        )}
      </div>
    </section>
  );
};

export default MarketStats;
